import {Button, DialogActions, DialogContent, DialogContentText, DialogTitle, List, ListItem, ListItemText, Typography} from "@mui/material";
import {Dialog2} from "../../../../../misc/Dialog2";
import {HardwareGroupWithContent} from "../../../../../api/Hardware";
import {HardwareReconcileReport} from "../model/hardware/MdHardware";

// shown on load when a hardware elevation drifted from the opening's current hardware group:
// out-of-group placements/hinge-overrides were removed and any missing primary handle/lock
// was auto-placed. Acknowledge-only (the reconcile already ran); the changes persist when the
// user saves. See MdHardware.reconcileToGroup.
export function HardwareReconcileDialog(props: {
    report: HardwareReconcileReport;
    hardwareGroup: HardwareGroupWithContent | null;
    onClose(): void;
}) {
    const nameFor = (productId: number) => {
        const item = props.hardwareGroup?.hardware.find(i => i.product === productId);
        return item ? (item.name || item.productCode || `#${productId}`) : `product #${productId} (no longer in the hardware schedule)`;
    };

    const {droppedPlacements, addedPrimaries, droppedHingeOverrides} = props.report;
    const droppedOverrideIds = Array.from(new Set(droppedHingeOverrides));

    return (
        <Dialog2 open onClose={props.onClose}>
            <DialogTitle>Hardware updated to match the schedule</DialogTitle>
            <DialogContent>
                <DialogContentText style={{minWidth: 420}}>
                    This opening's hardware schedule changed since the drawing was saved, so the
                    hardware drawing was reconciled to match. Review the changes and save to keep them.
                </DialogContentText>

                {droppedPlacements.length > 0 && (
                    <>
                        <Typography variant="subtitle2" sx={{mt: 2}}>Removed (no longer in the schedule)</Typography>
                        <List dense disablePadding>
                            {droppedPlacements.map((p, i) => (
                                <ListItem key={`drop-${i}`} disableGutters>
                                    <ListItemText primary={nameFor(p.productId)} secondary={`${p.kind} · ${p.target}`} />
                                </ListItem>
                            ))}
                        </List>
                    </>
                )}

                {droppedOverrideIds.length > 0 && (
                    <>
                        <Typography variant="subtitle2" sx={{mt: 2}}>Cleared hinge product overrides</Typography>
                        <List dense disablePadding>
                            {droppedOverrideIds.map(id => (
                                <ListItem key={`hinge-${id}`} disableGutters>
                                    <ListItemText primary={nameFor(id)} />
                                </ListItem>
                            ))}
                        </List>
                    </>
                )}

                {addedPrimaries.length > 0 && (
                    <>
                        <Typography variant="subtitle2" sx={{mt: 2}}>Added (schedule primary not yet drawn)</Typography>
                        <List dense disablePadding>
                            {addedPrimaries.map((p, i) => (
                                <ListItem key={`add-${i}`} disableGutters>
                                    <ListItemText primary={nameFor(p.productId)} secondary={p.kind} />
                                </ListItem>
                            ))}
                        </List>
                    </>
                )}
            </DialogContent>
            <DialogActions>
                <Button variant="contained" onClick={props.onClose}>Got it</Button>
            </DialogActions>
        </Dialog2>
    );
}
